import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import SortIcon from '@mui/icons-material/Sort';
import BedIcon from '@mui/icons-material/Bed';
import BathtubIcon from '@mui/icons-material/Bathtub';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import EuroIcon from '@mui/icons-material/Euro';
import HomeIcon from '@mui/icons-material/Home';
import { Button, Divider, IconButton, Switch, FormControlLabel, Menu, MenuItem } from '@mui/material';
import { ReactComponent as CollectionSvg } from 'assets/svg/CollectionOut.svg';
import { ReactComponent as DefaultPropertyImageSvg } from 'assets/svg/Default-Property-Image.svg';
import { ReactComponent as SeeDetailsMobileSvg } from 'assets/svg/Property-See-Details-Mobile.svg';
import { NoCompetitor } from 'components/CompetitorAdjusted';
import { AiPercentage } from 'components/Labels';
import NewLabel from 'components/Labels/NewCompetitor';
import { OneLetterAvatar } from 'components/LetterAvatar';
import getSymbolFromCurrency from 'currency-symbol-map';
import { useModals } from 'pages/MainProvider';
import { Fragment, useEffect, useState, memo, useCallback } from 'react';
import ReactCountryFlag from 'react-country-flag';
import useCollectionStore from 'store/useCollection';
import useUserStore from 'store/useUserStore';
import { DEFAULT_SORT } from 'util/Constants';
import { convertDateIntoLocaleString, formatNumber, GetCountryCode, isNew } from 'util/Funcs';
import { ORDER_TYPE, PropertyDTO, SortDTO } from 'util/Types';
import { Property } from 'types/Property';
import styles from './propertytable.module.css';
import { PropertyTableProps } from './types';

// Custom hook to validate images
const useImageValidation = (imageUrls: string[] = [], webPageUrl?: string) => {
    const [validImages, setValidImages] = useState<string[]>([]);
    const [isValidating, setIsValidating] = useState(false);

    // Handle image URL construction
    const getImageUrl = useCallback(
        (imagePath: string) => {
            if (!webPageUrl) return imagePath;
            try {
                const originUrl = new URL(webPageUrl);
                return imagePath.startsWith('http') ? imagePath : `${originUrl.origin}/${imagePath}`;
            } catch {
                return imagePath;
            }
        },
        [webPageUrl],
    );

    const validateImage = useCallback((url: string): Promise<boolean> => {
        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
                // Check if image has reasonable dimensions (not too small)
                if (img.width >= 100 && img.height >= 100) {
                    resolve(true);
                } else {
                    resolve(false);
                }
            };
            img.onerror = () => resolve(false);
            img.src = url;
        });
    }, []);

    useEffect(() => {
        if (!imageUrls || imageUrls.length === 0) {
            setValidImages([]);
            return;
        }

        setIsValidating(true);

        const validateImages = async () => {
            // Process images in sequence to find the first working one
            for (const imageUrl of imageUrls) {
                const fullUrl = getImageUrl(imageUrl);
                const isValid = await validateImage(fullUrl);
                if (isValid) {
                    setValidImages([fullUrl]);
                    setIsValidating(false);
                    return;
                }
            }

            // If no valid images found
            setValidImages([]);
            setIsValidating(false);
        };

        validateImages();
    }, [imageUrls, getImageUrl, validateImage]);

    return { validImages, isValidating };
};

interface SortHeaderItemProps {
    title: string;
    currentSort: SortDTO;
    className?: string;
    sortField?: string;
    onSort?: (sort: SortDTO) => void;
    'data-cy'?: string;
}

const SORT_TYPES: ORDER_TYPE[] = ['default', 'ASC', 'DESC'];
const SORT_ICONS = [<ArrowRightIcon />, <ArrowDropDownIcon />, <ArrowDropUpIcon />];

const HeaderItem = ({ className = '', title, currentSort, sortField = '', onSort = () => {}, 'data-cy': dataCy = '' }: SortHeaderItemProps) => {
    const [sortStatus, setSortStatus] = useState<number>(0);

    useEffect(() => {
        if (currentSort.field !== sortField) setSortStatus(0);
        else {
            setSortStatus(SORT_TYPES.indexOf(currentSort.order));
        }
    }, [currentSort]);

    const onSortClicked = () => {
        const newSortIndex = (sortStatus + 1) % 3;
        setSortStatus(newSortIndex);
        if (sortField) {
            onSort({
                field: sortField,
                order: SORT_TYPES[newSortIndex],
            });
        }
    };

    return (
        <div className={`flex items-center gap-[2px] cursor-pointer ${className}`} onClick={onSortClicked} data-cy={dataCy}>
            <span className={styles['headertitle']}>{title}</span>
            {sortField && <div>{SORT_ICONS[sortStatus]}</div>}
        </div>
    );
};

const MIN_W = 400;
const MIN_H = 200;

const getScreenshotUrl = (propertyId: string, webPageUrl?: string) => {
    return `https://cdnhellodatastore.azureedge.net/webpage-screenshots/${propertyId}.png`;
};

const DateItem = memo(({ property }: { property: PropertyDTO }) => {
    const isNewProperty: boolean = isNew(property.createdAt);
    const [screenshotError, setScreenshotError] = useState<boolean>(false);

    // Validate property images
    const { validImages, isValidating } = useImageValidation(property.imageUrls, property.webPageUrl);

    const handleScreenshotError = useCallback(() => {
        setScreenshotError(true);
    }, []);

    const screenshotUrl = getScreenshotUrl(property.propertyId, property.webPageUrl);

    return (
        <div className="relative flex gap-[10px] w-full">
            <div className="min-w-[130px] w-[130px] h-[90px] overflow-hidden flex items-center justify-center flex-shrink-0" data-cy="property-image">
                {isValidating ? (
                    <div className="w-full h-full bg-gray-100 animate-pulse"></div>
                ) : validImages.length > 0 ? (
                    <img src={validImages[0]} width={130} height={90} className="object-cover" loading="lazy" />
                ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                        <img
                            src={screenshotError ? getScreenshotUrl(property.propertyId) : screenshotUrl}
                            className="w-full h-full object-cover"
                            style={{ objectPosition: 'center 7%' }}
                            alt={property.title}
                            onError={handleScreenshotError}
                            loading="lazy"
                        />
                        <DefaultPropertyImageSvg style={{ display: 'none' }} />
                    </div>
                )}
            </div>

            <div className="flex flex-col justify-between min-w-0 max-w-[calc(100%-52px)] flex-1">
                <div className="h-[22px]"></div>
                <div className="font-medium w-full">
                    <span
                        data-cy="property-title"
                        className="text-[16px] block max-h-[2.5em] line-clamp-2 overflow-hidden text-ellipsis whitespace-normal display-[-webkit-box] [-webkit-line-clamp:2] [-webkit-box-orient:vertical] leading-tight"
                    >
                        {property.title}
                    </span>
                </div>
                <div className="flex items-center gap-2 h-[22px]">
                    {isNewProperty && (
                        <div style={{ transform: 'scale(0.9)' }}>
                            <NewLabel />
                        </div>
                    )}
                    <div className="text-[12px]" data-cy="property-date">
                        {convertDateIntoLocaleString(property.createdAt)}
                    </div>
                </div>
            </div>
        </div>
    );
});

interface SortOption {
    title: string;
    field: string;
    dataCy: string;
}

const SORT_OPTIONS: SortOption[] = [
    { title: 'Title & Publication Date', field: 'propertyCreatedAt', dataCy: 'listing-date-header' },
    { title: 'Collaboration Partner', field: 'CompanyName', dataCy: 'competitor-header' },
    { title: 'City', field: 'AddressCity', dataCy: 'city-header' },
    { title: 'Property Type', field: 'PropertyType', dataCy: 'property-type-header' },
    { title: 'Price', field: 'Price', dataCy: 'price-header' },
    { title: 'Price/m²', field: 'PricePerSquareMeter', dataCy: 'price-per-square-meter-header' },
    { title: 'Living size', field: 'SizeLand', dataCy: 'construction-area-header' },
    { title: 'Plot area', field: 'SizePlot', dataCy: 'plot-area-header' },
];

interface PropertyGridImageProps {
    property: PropertyDTO;
}

const PropertyGridImage = memo(({ property }: PropertyGridImageProps) => {
    const [screenshotError, setScreenshotError] = useState<boolean>(false);

    // Validate property images
    const { validImages, isValidating } = useImageValidation(property.imageUrls, property.webPageUrl);

    const handleScreenshotError = useCallback(() => {
        setScreenshotError(true);
    }, []);

    const screenshotUrl = getScreenshotUrl(property.propertyId, property.webPageUrl);

    return (
        <div className="relative">
            {isValidating ? (
                <div className="w-full h-[200px] bg-gray-100 animate-pulse"></div>
            ) : validImages.length > 0 ? (
                <img src={validImages[0]} className="w-full h-[200px] object-cover" alt={property.title} loading="lazy" />
            ) : (
                <div className="w-full h-[200px] bg-gray-100 flex items-center justify-center">
                    <img
                        src={screenshotError ? getScreenshotUrl(property.propertyId) : screenshotUrl}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: 'center 7%' }}
                        alt={property.title}
                        onError={handleScreenshotError}
                        loading="lazy"
                    />
                    <DefaultPropertyImageSvg style={{ display: 'none' }} />
                </div>
            )}
        </div>
    );
});

const PropertyTable = memo(({ propertyList, onSort, isForCompetitor, shouldShowDetail, isGridView }: PropertyTableProps) => {
    const { showAddPropertyToCollectionModal, showPropertyDetailModal, isAdditionalDetailOpened } = useModals();
    const { collectionList } = useCollectionStore();
    const { getUserPlan, userInfo } = useUserStore();
    const [currentSort, setCurrentSort] = useState<SortDTO>(DEFAULT_SORT);
    const [expandedPropertyId, setExpandedPropertyId] = useState<string | null>(null);
    const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);

    const onClickShowDetail = (property: PropertyDTO) => {
        showPropertyDetailModal(property, true);
    };

    const onSortClicked = (sort: SortDTO) => {
        setCurrentSort(sort);
        if (onSort) onSort(sort);
    };

    const onCollectionClicked = (propertyId: string) => {
        showAddPropertyToCollectionModal(propertyId);
    };

    const currentPlan = getUserPlan();

    const handleMobileDetailsClick = (propertyId: string) => {
        setExpandedPropertyId(expandedPropertyId === propertyId ? null : propertyId);
    };

    const hasAiScore = propertyList.some((property) => property.aiScore && property.aiScore >= 0);

    const handleSortMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setSortAnchorEl(event.currentTarget);
    };

    const handleSortMenuClose = () => {
        setSortAnchorEl(null);
    };

    const handleSortOptionClick = (option: SortOption) => {
        const newSortIndex = currentSort.field === option.field ? (SORT_TYPES.indexOf(currentSort.order) + 1) % 3 : 1;
        const newSort = {
            field: option.field,
            order: SORT_TYPES[newSortIndex],
        };
        setCurrentSort(newSort);
        onSort(newSort);
        handleSortMenuClose();
    };

    const getSortIcon = (field: string) => {
        if (currentSort.field !== field) return <ArrowRightIcon />;
        return SORT_ICONS[SORT_TYPES.indexOf(currentSort.order)];
    };

    return (
        <>
            <div className={`w-full font-medium ${styles['propertytable']} lg:bg-white rounded-b-[10px]`}>
                {/* Sort Menu for Grid View */}
                {isGridView && (
                    <div className="flex justify-end py-4 lg:px-4 md:px-0 sm:px-0">
                        <Button
                            variant="outlined"
                            startIcon={<SortIcon />}
                            endIcon={currentSort.field ? getSortIcon(currentSort.field) : null}
                            onClick={handleSortMenuOpen}
                            className="!border-my-blue !text-my-blue"
                        >
                            {currentSort.field ? SORT_OPTIONS.find((opt) => opt.field === currentSort.field)?.title : 'Sort by'}
                        </Button>
                        <Menu anchorEl={sortAnchorEl} open={Boolean(sortAnchorEl)} onClose={handleSortMenuClose}>
                            {SORT_OPTIONS.map((option) => (
                                <MenuItem key={option.field} onClick={() => handleSortOptionClick(option)} data-cy={option.dataCy}>
                                    <div className="flex items-center gap-2">
                                        {getSortIcon(option.field)}
                                        {option.title}
                                    </div>
                                </MenuItem>
                            ))}
                        </Menu>
                    </div>
                )}

                {/* Desktop Table Header */}
                {!isGridView && (
                    <div className="hidden lg:block">
                        <div
                            className={`${styles['tr']} ${styles['thead']} grid ${isForCompetitor ? 'xl:grid-cols-9 grid-cols-8' : 'xl:grid-cols-8 grid-cols-7'} items-center text-my-black text-opacity-60 border-y border-my-gray border-opacity-40 py-[15px] px-[10px] bg-my-gray bg-opacity-20 xl:flex xl:w-full xl:items-center w-full`}
                        >
                            <div className="xl:w-[35%] xl:flex xl:items-center w-full">
                                <HeaderItem
                                    title="Title & Publication Date"
                                    sortField="propertyCreatedAt"
                                    currentSort={currentSort}
                                    onSort={onSortClicked}
                                    className={`${hasAiScore ? 'xs:hidden flex' : 'flex'} `}
                                    data-cy="listing-date-header"
                                />
                            </div>
                            <div className="xl:w-[15%] xl:flex xl:items-center w-full">
                                {isForCompetitor && (
                                    <HeaderItem
                                        title="Collaboration Partner"
                                        sortField="CompanyName"
                                        currentSort={currentSort}
                                        onSort={onSortClicked}
                                        data-cy="competitor-header"
                                        className={`${hasAiScore ? 'xs:!flex' : 'xs:hidden flex'} `}
                                    />
                                )}
                            </div>
                            <div className="xl:w-[12%] xl:flex xl:items-center w-full">
                                <HeaderItem title="City" sortField="AddressCity" currentSort={currentSort} onSort={onSortClicked} data-cy="city-header" />
                            </div>
                            <div className="xl:w-[12%] xl:flex xl:items-center w-full">
                                <HeaderItem
                                    title="Property Type"
                                    sortField="PropertyType"
                                    currentSort={currentSort}
                                    onSort={onSortClicked}
                                    data-cy="property-type-header"
                                />
                            </div>
                            <div className="xl:w-[12%] xl:flex xl:items-center w-full">
                                <HeaderItem title="Price" sortField="Price" currentSort={currentSort} onSort={onSortClicked} data-cy="price-header" />
                            </div>
                            <div className="xl:w-[12%] xl:flex xl:items-center w-full">
                                <HeaderItem
                                    title="Price/m²"
                                    sortField="PricePerSquareMeter"
                                    currentSort={currentSort}
                                    onSort={onSortClicked}
                                    data-cy="price-per-square-meter-header"
                                />
                            </div>
                            <div className="xl:w-[10%] xl:flex xl:items-center w-full">
                                <HeaderItem
                                    title="Living size"
                                    sortField="SizeLand"
                                    currentSort={currentSort}
                                    onSort={onSortClicked}
                                    data-cy="construction-area-header"
                                />
                            </div>
                            <div className="xl:w-[10%] xl:flex xl:items-center w-full">
                                <HeaderItem
                                    title="Plot area"
                                    sortField="SizePlot"
                                    currentSort={currentSort}
                                    onSort={onSortClicked}
                                    className={`xl:flex hidden ${isForCompetitor ? 'xl:col-span-2' : 'col-span-1'}`}
                                    data-cy="plot-area-header"
                                />
                            </div>
                            <div className="xl:w-[6%] xl:flex xl:items-center w-full">{/* Empty header for buttons */}</div>
                        </div>
                    </div>
                )}

                {userInfo?.savedCompetitors.length === 0 ? (
                    <NoCompetitor />
                ) : (
                    <>
                        {/* Shared container for both views */}
                        <div className={`relative`} data-cy="property-list-container">
                            <div
                                className={`transition-all duration-300 ease-in-out ${
                                    isGridView ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none hidden'
                                }`}
                            >
                                <div
                                    className={`grid grid-cols-1 [@media(min-width:600px)]:grid-cols-2 ${isForCompetitor ? '' : '[@media(min-width:980px)]:grid-cols-3'} gap-4 py-4 lg:px-4 md:px-0 sm:px-0`}
                                >
                                    {propertyList.map((property: PropertyDTO | Property, index: number) => {
                                        const aiScore = Math.floor((property?.aiScore || -1) * 100);
                                        const isInAnyCollection = collectionList?.some((collection) =>
                                            collection?.properties?.some((p) => p.propertyId === property.propertyId),
                                        );

                                        return (
                                            <div
                                                key={property.propertyId}
                                                className="transition-all duration-200 ease-in-out hover:shadow-md cursor-pointer"
                                                style={{
                                                    willChange: 'transform',
                                                    transform: 'translateZ(0)', // Hardware acceleration
                                                }}
                                                onClick={() => onClickShowDetail(property as PropertyDTO)}
                                            >
                                                <div className="bg-white border border-my-gray border-opacity-60 rounded-lg shadow-sm overflow-hidden">
                                                    {/* Image Section */}
                                                    <div className="relative">
                                                        <PropertyGridImage property={property as PropertyDTO} />
                                                        <div className="absolute top-3 right-3 flex items-center gap-2">
                                                            {aiScore >= 0 && <AiPercentage percentage={aiScore} />}
                                                            {userInfo && (
                                                                <IconButton
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        onCollectionClicked(property.propertyId);
                                                                    }}
                                                                    className="bg-white/80 hover:bg-white/90"
                                                                >
                                                                    <CollectionSvg className={isInAnyCollection ? 'text-red-500' : 'text-my-gray'} />
                                                                </IconButton>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Content Section */}
                                                    <div className="p-4">
                                                        {/* Company Name with Rental/Sales Icon - Only for logged in users */}
                                                        {userInfo && (
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <span
                                                                    className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium ${
                                                                        property.details.listingType === 'rental'
                                                                            ? 'bg-green-100 text-green-600'
                                                                            : 'bg-blue-100 text-blue-600'
                                                                    }`}
                                                                >
                                                                    {property.details.listingType === 'rental' ? 'R' : 'S'}
                                                                </span>
                                                                <span className="text-sm font-medium text-gray-600">{property.companyName || ''}</span>
                                                            </div>
                                                        )}

                                                        <div className="h-[48px] mb-4">
                                                            <h3 className="text-lg font-semibold line-clamp-2 flex items-center">
                                                                {!userInfo && (
                                                                    <span
                                                                        className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium mr-2 flex-shrink-0 ${
                                                                            property.details.listingType === 'rental'
                                                                                ? 'bg-green-100 text-green-600'
                                                                                : 'bg-blue-100 text-blue-600'
                                                                        }`}
                                                                    >
                                                                        {property.details.listingType === 'rental' ? 'R' : 'S'}
                                                                    </span>
                                                                )}
                                                                <span className="line-clamp-2">{property.title}</span>
                                                            </h3>
                                                        </div>

                                                        <div className="grid grid-cols-2 gap-4 mb-4">
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <EuroIcon className="text-gray-500 w-5 h-5" />
                                                                    <div className="font-medium">
                                                                        {property.details.price && property.details.price > 0
                                                                            ? `€ ${formatNumber(property.details.price)}`
                                                                            : 'On Request'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    {property.address.Country && (
                                                                        <ReactCountryFlag
                                                                            svg
                                                                            countryCode={GetCountryCode(property.address.Country || '') || ''}
                                                                            style={{ width: '1em', height: '1em' }}
                                                                            className="w-4 h-4"
                                                                        />
                                                                    )}
                                                                    <div className="font-medium ml-2">{property.address.City}</div>
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <HomeIcon className="text-gray-500 w-5 h-5" />
                                                                    <div className="font-medium">{property.details.propertyType || ''}</div>
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    {property.details.sizePlot ? (
                                                                        <>
                                                                            <SquareFootIcon className="text-gray-500 w-5 h-5" />
                                                                            <div className="font-medium">{`${formatNumber(property.details.sizePlot)} m²`}</div>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <HomeIcon className="text-gray-500 w-5 h-5" />
                                                                            <div className="font-medium">{`${formatNumber(property.details.sizeLiving || 0)} m²`}</div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <BedIcon className="text-gray-500 w-5 h-5" />
                                                                    <div className="font-medium">{property.details.bedrooms || '0'}</div>
                                                                </div>
                                                            </div>
                                                            <div>
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <BathtubIcon className="text-gray-500 w-5 h-5" />
                                                                    <div className="font-medium">{property.details.bathrooms || '0'}</div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div className="text-sm text-gray-500">{convertDateIntoLocaleString(property.createdAt)}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <div
                                className={`transition-all duration-300 ease-in-out ${
                                    !isGridView ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none hidden'
                                }`}
                            >
                                <div className="flex flex-col gap-[4px]">
                                    {propertyList.map((property: PropertyDTO | Property, index: number) => {
                                        const aiScore = Math.floor((property?.aiScore || -1) * 100);
                                        const isInAnyCollection = collectionList?.some((collection) =>
                                            collection?.properties?.some((p) => p.propertyId === property.propertyId),
                                        );

                                        return (
                                            <div
                                                key={property.propertyId}
                                                className="transition-all duration-200 ease-in-out hover:bg-gray-50 cursor-pointer"
                                                style={{
                                                    willChange: 'transform',
                                                    transform: 'translateZ(0)', // Hardware acceleration
                                                }}
                                                onClick={() => onClickShowDetail(property as PropertyDTO)}
                                            >
                                                <div
                                                    className={`${styles['tr']} grid ${isForCompetitor ? 'xl:grid-cols-9 grid-cols-8' : 'xl:grid-cols-8 grid-cols-7'} items-center border-opacity-40 py-[8px] px-[10px] xl:flex xl:w-full xl:items-center bg-white`}
                                                    data-cy={`property-row-${index}`}
                                                >
                                                    <div className="xl:w-[35%] xl:flex xl:items-center col-span-2 pr-5">
                                                        <DateItem property={property as PropertyDTO} />
                                                    </div>
                                                    <div className={`xl:w-[15%] xl:flex xl:items-center ${isForCompetitor ? 'col-span-1' : 'hidden'} pr-5`}>
                                                        <div className={`flex items-center gap-[5px]`}>
                                                            <div className="flex">
                                                                <OneLetterAvatar
                                                                    title={property.details.listingType === 'rental' ? 'Rental' : 'Sales'}
                                                                    colorClass={property.details.listingType === 'rental' ? 'text-my-green' : 'text-my-blue'}
                                                                />
                                                            </div>
                                                            {isForCompetitor && <span data-cy="property-company">{property.companyName || ''}</span>}
                                                        </div>
                                                    </div>
                                                    <div className="xl:w-[12%] xl:flex xl:items-center col-span-1 gap-[5px] pr-5" data-cy="property-city">
                                                        {property.address.Country && (
                                                            <ReactCountryFlag
                                                                svg
                                                                countryCode={GetCountryCode(property.address.Country || '') || ''}
                                                                style={{
                                                                    width: '1em',
                                                                    height: '1em',
                                                                }}
                                                                className="w-4 h-4"
                                                            />
                                                        )}
                                                        <div className="font-medium ml-2">{property.address.City}</div>
                                                    </div>
                                                    <div
                                                        className="xl:w-[12%] xl:flex xl:items-center col-span-1 pr-5"
                                                        data-cy="property-type"
                                                    >{`${property.details.propertyType || ''}`}</div>
                                                    <div className="xl:w-[12%] xl:flex xl:items-center col-span-1" data-cy="property-price">
                                                        {property.details.price && property.details.price > 0
                                                            ? `€ ${formatNumber(property.details.price)}`
                                                            : 'On Request'}
                                                    </div>
                                                    <div className="xl:w-[12%] xl:flex xl:items-center col-span-1" data-cy="property-price-per-square-meter">
                                                        {property.details.pricePerSquareMeter && property.details.pricePerSquareMeter > 0
                                                            ? `€ ${formatNumber(Math.round(property.details.pricePerSquareMeter))}/m²`
                                                            : '-'}
                                                    </div>
                                                    <div
                                                        className="xl:w-[10%] xl:flex xl:items-center col-span-1"
                                                        data-cy="property-constructed-area"
                                                    >{`${formatNumber(property.details.sizeLiving || 0)} m²`}</div>
                                                    <div className="xl:w-[10%] xl:flex xl:items-center col-span-1">
                                                        <div className="xl:inline-block hidden" data-cy="property-plot-area">
                                                            {`${formatNumber(property.details.sizePlot || 0)} m²`}
                                                        </div>
                                                    </div>
                                                    <div className="xl:w-[6%] xl:flex xl:items-center col-span-1">
                                                        <div className="flex items-center text-my-blue justify-end gap-2">
                                                            <div className="flex items-center gap-2">
                                                                {aiScore >= 0 && <AiPercentage percentage={aiScore} />}
                                                                {userInfo && (
                                                                    <IconButton
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            onCollectionClicked(property.propertyId);
                                                                        }}
                                                                    >
                                                                        <CollectionSvg className={isInAnyCollection ? 'text-red-500 ' : 'text-my-gray'} />
                                                                    </IconButton>
                                                                )}
                                                            </div>

                                                            <div
                                                                className={`lg:hidden w-[50px] h-[48px] flex items-center justify-center border ${property.propertyId === expandedPropertyId ? 'bg-my-blue text-white' : 'border-my-blue text-my-blue'} cursor-pointer`}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleMobileDetailsClick(property.propertyId);
                                                                }}
                                                            >
                                                                <SeeDetailsMobileSvg
                                                                    className={property.propertyId === expandedPropertyId ? 'rotate-180' : ''}
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <Divider />
                                                {/* Mobile Details */}
                                                {expandedPropertyId === property.propertyId && (
                                                    <div className="border border-gray-100 lg:hidden">
                                                        <div>
                                                            <div className={`${styles['hiddenDisplay']} flex border-b bg-gray-50 border-gray-200`}>
                                                                <div className="p-3 flex-1 pr-6 border-r border-gray-300">
                                                                    <InfoRow label="Country" value={property.address.Country} />
                                                                    <InfoRow label="City" value={property.address.City} />
                                                                    <InfoRow label="Property Type" value={property.details.propertyType} />
                                                                </div>
                                                                <div className="p-3 flex-1 pl-6">
                                                                    <InfoRow label="Price" value={`€ ${formatNumber(property.details.price || 0)}`} />
                                                                    <InfoRow
                                                                        label="Constructed area"
                                                                        value={`${formatNumber(property.details.sizeLiving || 0)} m²`}
                                                                    />
                                                                    <InfoRow label="Plot area" value={`${formatNumber(property.details.sizePlot || 0)} m²`} />
                                                                </div>
                                                            </div>
                                                            <div className="p-3">
                                                                <h1 className="text-lg font-semibold text-my-blue mb-1">About the property</h1>
                                                                <div className="relative">
                                                                    <div className="absolute inset-0 pointer-events-none" />
                                                                    <p className="text-gray-500 leading-relaxed">{property.description}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </>
    );
});

export default PropertyTable;

function InfoRow({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex justify-between mb-4 last:mb-0">
            <span className="text-sm text-gray-700">{label}</span>
            <span className="text-sm font-semibold">{value}</span>
        </div>
    );
}
